import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = useMemo(() => [
    {
      id: 'payment-methods',
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and secure online payments through Stripe. We do not currently accept insurance, HSA, or FSA cards."
    },
    {
      id: 'bulk-ordering',
      question: "Do you offer bulk ordering discounts?",
      answer: "Yes! We offer special pricing for bulk orders. Please contact us at info@medicalsupplyandmore.com or call (407) 415-3024 for a custom quote on large volume orders."
    },
    {
      id: 'return-policy',
      question: "What is your return policy?",
      answer: "We accept returns within 30 days of purchase for unopened, unused products in original packaging. Medical supplies and PPE items cannot be returned once opened for health and safety reasons. Please contact us to initiate a return."
    },
    {
      id: 'shipping-time',
      question: "How long does shipping take?",
      answer: "Standard shipping typically takes 3-5 business days. Expedited shipping options are available at checkout. Florida orders may arrive sooner. You'll receive tracking information once your order ships."
    },
    {
      id: 'fda-approved',
      question: "Are your products FDA approved?",
      answer: "Yes, all our medical supplies meet FDA standards and regulations. We source our products from trusted manufacturers and ensure quality compliance for all healthcare products."
    },
    {
      id: 'nationwide-shipping',
      question: "Do you ship nationwide?",
      answer: "Yes, we ship to all 50 states. Shipping costs are calculated based on your location and order weight at checkout."
    },
    {
      id: 'track-order',
      question: "Can I track my order?",
      answer: "Absolutely! Once your order ships, you'll receive an email with tracking information. You can also view your order status by logging into your account and visiting the Orders page."
    },
    {
      id: 'healthcare-facilities',
      question: "Do you serve healthcare facilities?",
      answer: "Yes! We serve clinics, hospitals, caregivers, home care providers, and individual customers. Contact us for special healthcare facility pricing and bulk ordering options."
    },
    {
      id: 'out-of-stock',
      question: "What if a product is out of stock?",
      answer: "If a product is temporarily out of stock, you can contact us to check availability or request a notification when it's back in stock. We restock popular items regularly."
    },
    {
      id: 'customer-support',
      question: "How can I contact customer support?",
      answer: "You can reach us by email at info@medicalsupplyandmore.com, call us at (407) 415-3024 during business hours (Mon-Fri 8am-8pm, Sat 9am-5pm), or use the contact form on our Contact page."
    }
  ], []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-medical-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-heading font-semibold mb-4">Frequently Asked Questions</h1>
          <p className="text-white/90">Find answers to common questions about our products and services</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={faq.id} className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`faq-item-${faq.id}`}>
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                data-testid={`faq-question-${faq.id}`}
              >
                <span className="font-medium text-left pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-medical-primary flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4 text-gray-600 leading-relaxed" data-testid={`faq-answer-${faq.id}`}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-medical-accent rounded-xl p-8 text-center">
          <h3 className="text-2xl font-heading font-semibold mb-2">Still have questions?</h3>
          <p className="text-gray-600 mb-4">We're here to help! Contact our support team for assistance.</p>
          <a href="/contact" className="inline-block bg-medical-primary text-white px-6 py-3 rounded-lg hover:bg-medical-primary/90 transition-colors" data-testid="contact-us-link">
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;